import React, { useRef, useState } from 'react';
import axiosInstance from '../../Axios';
import Toast from '../../services/Toast';
import { manageLoginData } from '../../services/AuthService';
import { useBrand } from '../../hooks/useBrand';
import ProductsTagline from '../products-tagline/ProductsTagline';
import './TwoFaForm.css';

const OTP_LENGTH = 6;

const TwoFaForm: React.FC<any> = ({ loginResponse }) => {
  const [otp, setOtp] = useState<string[]>(new Array(OTP_LENGTH).fill(''));
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [keyIndex , setKeyIndex] = useState(0);
  const { brand, isBranded, style: brandStyle } = useBrand();

  const handleChange = (index: number, value: string) => {
    let otpValue = [...otp];
    if (value.toString().length > 1) {
      if(otpValue[index]){
        otpValue[index] = value.toString()[1];
        inputRefs.current[index+1]?.focus();
        setOtp(otpValue);
        return ;
      }
      for (let i = 0; i < value.toString().length; i++) {
        if(index == 5){
          inputRefs.current[index]?.focus()
          break ;
        }
        otpValue[i] = value.toString()[i];
          inputRefs.current[i+1]?.focus();
        setOtp(otpValue);
      }
      // setOtp(otpValue);
    } else {
      if (!/^\d?$/.test(value)) return;
      otpValue[index] = value.toString()[0];
      setOtp(otpValue);
      if (value && index < OTP_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };
  const handleVerify = async () => {
    if (otp.includes('')) {
      Toast.error('Please enter the complete 6-digit code from your email.');
      return;
    }

    try {
      setLoading(true);
      const payload = { otp: otp.join(''), id: loginResponse.id };
      const response = await axiosInstance.post('/auth/verify-otp', payload);
      if (!response?.data?.status) {
        Toast.error('Something went wrong. Please try again later.');
        setLoading(false);
        return;
      }
      // No application argument: manageLoginData resolves the handoff target
      // from storage, which survives a reload of this OTP step.
      manageLoginData(response?.data?.data);
    } catch (error) {
      setLoading(false);
      setOtp(new Array(OTP_LENGTH).fill(''));
    }
  };
  const resendOtp = async () => {
    try {
      setResendLoading(true);
      const payload = { id: loginResponse.id };
      const response = await axiosInstance.post('/auth/resend-otp', payload);
      if (!response?.data?.status) {
        Toast.error('Something went wrong. Please try again later.');
        setResendLoading(false);
        return;
      }
      Toast.success('A new OTP has been sent to your registered email address.');
      setResendLoading(false);
      return true;
    } catch (error) {
      setResendLoading(false);
    }
  };
  return (
    <div className="twofa-card" style={brandStyle} data-brand={brand.key || 'nextek'}>
      <div className="twofa-card__inner">
        {/* Decorative elements */}
        <div className="twofa-card__decoration">
          <div className="twofa-card__circle twofa-card__circle--1" />
          <div className="twofa-card__circle twofa-card__circle--2" />
          <div className="twofa-card__dots">
            {[...Array(6)].map((_, i) => (
              <span key={i} style={{ animationDelay: `${i * 0.1}s` }} />
            ))}
          </div>
        </div>

        {/* Icon */}
        <div className="twofa-card__icon">
          <div className="twofa-card__icon-bg">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              <circle cx="12" cy="16" r="1"/>
            </svg>
          </div>
          <div className="twofa-card__shield">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
          </div>
        </div>

        {/* Header */}
        <div className="twofa-card__header">
          <h1 className="twofa-card__title">
            <span className="twofa-card__title-accent">Two-Factor</span> Verification
          </h1>
          <p className="twofa-card__subtitle">
            We've sent a 6-digit code to your email
          </p>
          <div className="twofa-card__email">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="4" width="20" height="16" rx="2"/>
              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
            </svg>
            <span>{loginResponse?.email}</span>
          </div>
        </div>

        {/* OTP Form */}
        <form className="twofa-form" onSubmit={(e) => e.preventDefault()} data-cy="twofa-form">
          <div className="twofa-otp-group">
            {otp.map((digit, index) => (
              <div key={index} className={`twofa-otp-input ${digit ? 'twofa-otp-input--filled' : ''}`}>
                <input
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={digit || ''}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  autoComplete="off"
                  placeholder="·"
                  data-cy={`twofa-otp-input-${index}`}
                />
                <div className="twofa-otp-input__highlight" />
              </div>
            ))}
          </div>

          <button
            type="button"
            disabled={loading}
            onClick={handleVerify}
            className={`twofa-btn ${loading ? 'twofa-btn--loading' : ''}`}
            data-cy="twofa-verify-btn"
          >
            {loading ? (
              <div className="twofa-btn__spinner">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" strokeOpacity="0.25"/>
                  <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/>
                </svg>
              </div>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                Verify Code
              </>
            )}
          </button>
        </form>

        {/* Resend */}
        <div className="twofa-resend">
          <p>Didn't receive the code?</p>
          <button
            type="button"
            className="twofa-resend-btn"
            onClick={resendOtp}
            disabled={resendLoading}
            data-cy="twofa-resend-btn"
          >
            {resendLoading ? (
              <span className="twofa-resend-btn__loading">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" strokeOpacity="0.25"/>
                  <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/>
                </svg>
                Sending...
              </span>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
                </svg>
                Resend Code
              </>
            )}
          </button>
        </div>

        {/* Footer */}
        <div className="twofa-footer">
          <ProductsTagline signingInTo={isBranded ? brand.name : null} />
        </div>
      </div>
    </div>
  );
};


export default TwoFaForm;
